import type { PropertyCardAddress } from '~/types/propertyCard';

/**
 * Configuration for formatting property address display
 */
export interface AddressDisplayOptions {
  locationFiltered: boolean;
}

/**
 * Result of address formatting with display instructions
 */
export interface AddressDisplayResult {
  /** Primary address text to display (street + number, optionally with district) */
  primary: string;
  /** Whether to show city badge in list view */
  showCityBadge: boolean;
  /** City name for badge (only when showCityBadge is true) */
  cityName?: string;
}

/**
 * Format property address for display based on filter context
 * 
 * Logic:
 * - When NOT location-filtered: Show city badge + street address
 * - When location-filtered: Show street + district (if available)
 * 
 * @param property - Property with address data
 * @param options - Display context options
 * @returns Formatted address display configuration
 */
export function formatPropertyAddress(
  property: { address?: PropertyCardAddress | null },
  options: AddressDisplayOptions
): AddressDisplayResult {
  const { address } = property;
  
  if (!address) {
    return {
      primary: 'Address unavailable',
      showCityBadge: false,
    };
  }

  const { street, houseNumber, district, cityName } = address;
  const { locationFiltered } = options;
  
  // Build base street address
  const streetAddress = `${street}${houseNumber ? ` ${houseNumber}` : ''}`.trim();
  
  if (locationFiltered) {
    // Location-filtered: Show street + district (if available)
    return {
      primary: district 
        ? `${streetAddress}, ${district}`
        : streetAddress,
      showCityBadge: false,
    };
  } else {
    // Discovery mode: Show city badge + street only
    return {
      primary: streetAddress,
      showCityBadge: true,
      cityName,
    };
  }
}
